<template>
  <div class="navbar">
    <div class="headerTitle">{{ $t(localeConsts.navTitle) }}</div>
    <div class="horizontalMenu">
      <b-form-select
        v-if="$store.state.loggedIn && !isProjectApplication && (getIsSupervisor || isViewerOrAdmin)"
        v-model="selected"
        :options="options.length < 1 ? [{ value: null, text: 'System-Übersicht' }] : options"
        class="project"
      >
      </b-form-select>

      <div
        v-else-if="!(getIsSupervisor || isViewerOrAdmin) && $store.state.loggedIn && !isProjectApplication"
        id="projectNameWithoutSelectionOption"
        class="project"
      >
        {{ getProjectName }}
      </div>

      <b-button>
        <div class="languageDropdown">
          <i class="bi bi-globe icon" />
          <LocaleSwitcher />
        </div>
      </b-button>

      <b-button v-if="$store.state.loggedIn" type="submit" @click="logout">
        <em class="bi bi-box-arrow-in-right icon"></em>
        {{ firstName }}
        {{ $t(localeConsts.navSignOut) }}
      </b-button>

      <b-button v-else type="submit" @click="pushToLogin">
        <em class="bi bi-box-arrow-in-right icon"></em>
        {{ $t(localeConsts.navLogin) }}
      </b-button>
    </div>
  </div>
</template>

<script>
import LocaleSwitcher from '@/components/util/LocaleSwitcher'
import axios from '../../../axios'
import mapGettersMixin from '../../mixins/mapGettersMixin'
import { mapActions, mapMutations } from 'vuex'

export default {
  name: 'NavBarComponent',
  components: { LocaleSwitcher },
  mixins: [mapGettersMixin],
  data() {
    return {
      localeConsts: {
        navTitle: 'NAV.TITLE',
        navSignOut: 'NAV.SIGN_OUT',
        navLogin: 'LOGIN.LOG_IN'
      },
      selected: null,
      options: [{ value: null, text: 'Lade Projekte...' }]
    }
  },
  computed: {
    isProjectApplication() {
      return this.currentRoute == 'ProjectApplication' //eslint-disable-line
    },
    currentRoute() {
      return this.$route.name
    },
    firstName() {
      return this.getFirstName
    },
    isViewerOrAdmin() {
      return this.$store.getters['authentication/getIsViewer'] || this.$store.getters['authentication/getIsAdministrator']
    }
  },
  watch: {
    selected(newValue) {
      this.setProjectId(newValue)
      this.fetchProjectName()
    },
    '$store.state.loggedIn': {
      immediate: true,
      async handler(isLoggedIn) {
        if (isLoggedIn) {
          await this.fetchProjects()

          const storedProjectId = this.$store.getters['authentication/getProjectId']
          const projectExists = this.options.some((opt) => opt.value === storedProjectId)

          if (storedProjectId && projectExists) {
            this.selected = storedProjectId
          } else {
            // Wenn kein gültiges Projekt gewählt ist, nimm den ersten Eintrag aus der Liste.
            // Bei reinen Supervisoren ist dies nun das erste zugeteilte Projekt,
            // bei Admins/Viewern ist es null ("System-Übersicht").
            this.selected = this.options.length > 0 ? this.options[0].value : null
          }
        } else {
          this.options = [{ value: null, text: 'System-Übersicht' }]
          this.selected = null
        }
      }
    }
  },

  async beforeMount() {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const res = await axios.get('api/user/login/', { params: { token } })
        if (res.status === 200) {
          this.$store.state.loggedIn = true
        } else {
          this.handleFailedLogin()
        }
      } catch (error) {
        this.handleFailedLogin()
      }
    } else {
      this.handleFailedLogin()
    }
  },

  methods: {
    ...mapActions('authentication', ['fetchProjectName']),
    ...mapMutations('authentication', ['setProjectId']),

    handleFailedLogin() {
      localStorage.clear()
      this.$store.state.loggedIn = false
      this.pushToLogin()
    },

    async logout() {
      try {
        const response = await axios.get('api/user/logout/', {
          headers: { 'Content-Type': 'application/json' }
        })
        if (response.status === 205) {
          this.handleFailedLogin()
        }
      } catch (error) {
        this.handleFailedLogin()
      }
    },

    pushToLogin() {
      this.$router.push('login').catch((err) => {
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Avoided redundant navigation')) {
          throw err
        }
      })
    },

    async fetchProjects() {
      const loadedOptions = []

      try {
        if (this.isViewerOrAdmin) {
          const response = await axios.get('projects/')
          response.data.forEach((project) => {
            loadedOptions.push({ value: project.id, text: project.name })
          })
          // Admins und Viewer behalten die "System-Übersicht"
          this.options = [{ value: null, text: 'System-Übersicht' }, ...loadedOptions]
        } else if (this.getIsSupervisor) {
          const response = await axios.get(`user-project-attributions/`)
          const processedProjects = new Set()

          for (const roleAttribution of response.data) {
            const projectId = typeof roleAttribution.project === 'object' ? roleAttribution.project.id : roleAttribution.project

            if (!processedProjects.has(projectId)) {
              processedProjects.add(projectId)
              const projectNameRes = await axios.get(`projects/${projectId}/`)
              loadedOptions.push({ value: projectId, text: projectNameRes.data.name })
            }
          }

          // Reine Supervisoren: "System-Übersicht" nur anzeigen, wenn es noch gar keine Projekte gibt.
          if (loadedOptions.length > 0) {
            this.options = loadedOptions
          } else {
            this.options = [{ value: null, text: 'System-Übersicht' }]
          }
        } else {
          this.options = []
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Fehler beim Laden der Projekte für das Dropdown:', error)
        this.options = [{ value: null, text: 'System-Übersicht' }] // Fallback bei Error
      }
    }
  }
}
</script>

<style>
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #8b69a9;
  width: stretch;
}

.headerTitle {
  margin-left: 10px;
  font-size: 35px;
  color: white;
}

.horizontalMenu {
  display: flex;
  flex-direction: row;
  margin-right: 10px;
  align-content: center;
}

.icon {
  font-size: 25px;
}

.horizontalMenu img {
  margin-left: 5px;
  margin-right: 5px;
}

.project {
  padding-top: 7px;
  background-color: #8b69a9;
  color: white;
}

#projectNameWithoutSelectionOption {
  display: grid;
  place-items: center;
}

.languageDropdown {
  display: flex;
  justify-items: center;
  align-items: center;
}
</style>
